import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, DialogV2, SelectField } from 'c-components';
import { useAPIClientRequest, useLanguageSwitcher, useSkinSwitcher } from 'c-hooks';
import ApiClient from 'c-data/apiClient';
import AdminSkinSwitcher from 'c-wrapper/Components/SkinLangSwitcher/AdminSkinSwitcher';
import { DEFAULT_SKIN_ID } from 'c-wrapper/Slices/UI/ui-selectors';
import { useCommonTranslation } from 'c-translation';
import { Stack, TextField } from '@mui/material';
import { NetworkRequestState } from 'c-data-layer';
import { PermissionName } from 'c-sdk';
import { LANG_en_gb } from 'c-lib';
import { DateRangePicker } from '@mui/x-date-pickers-pro';
import { useBoolean } from 'react-hanger';
import { IfHasAllPermissions } from 'c-auth-module/Components';
import { useUserPermissions } from 'c-auth-module/Hooks';

type BooleanState = {
    value: boolean;
    toggle: () => void;
    setTrue: () => void;
    setFalse: () => void;
};

interface Props {
    id: string | number;
    pcaState: BooleanState;
    isLinear: boolean;
    campaignStartDate: string | null;
    campaignEndDate: string | null;
    reportTypeToSend: 'pca' | 'inflight';
    dialogTitle: string;
    initiatedEmails: string[];
    onSendSuccess?: () => void;
}

type ReportFormat = 'pdf' | 'powerpoint';

const pdfPerms = [PermissionName.PdfPcaReportRead];
const pptPerms = [PermissionName.PowerpointPcareportRead];

function formatDateToDDMMYYYY(date: Date | null): string {
    if (!date) return '';
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
}

const parseEmails = (raw: string) =>
    raw
        .split('\n')
        .map(v => v.trim())
        .filter(v => v.length > 0);

const DataStatusSendReportDialog: React.FC<Props> = ({
    id,
    pcaState,
    isLinear,
    campaignStartDate,
    campaignEndDate,
    reportTypeToSend,
    dialogTitle,
    initiatedEmails = [],
    onSendSuccess,
}) => {
    const t = useCommonTranslation();
    const { selectedSkin } = useSkinSwitcher();
    const { hasAll } = useUserPermissions();
    const adminPerms = useMemo(() => hasAll([PermissionName.Admin]), [hasAll]);
    const canSendPdf = useMemo(() => hasAll(pdfPerms), [hasAll]);
    const canSendPpt = useMemo(() => hasAll(pptPerms), [hasAll]);
    const { lang } = useLanguageSwitcher();

    const [skin, setSkin] = useState<number | null>(selectedSkin?.id ?? null);
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
        campaignStartDate ? new Date(campaignStartDate) : null,
        campaignEndDate ? new Date(campaignEndDate) : null,
    ]);
    const thirdColor = useBoolean(false);
    const [useDateRange, setUseDateRange] = useState(0);
    const [language, setLanguage] = useState<'GB' | 'US'>(lang === LANG_en_gb ? 'GB' : 'US');
    const [reportType, setReportType] = useState<'Linear_report' | 'DSP_report' | 'custom'>(
        isLinear ? 'Linear_report' : 'DSP_report',
    );
    const [emailsText, setEmailsText] = useState(initiatedEmails.join('\n'));
    const [localError, setLocalError] = useState<string | null>(null);
    const [sentSuccessNotified, setSentSuccessNotified] = useState(false);
    const [format, setFormat] = useState<ReportFormat>(canSendPdf ? 'pdf' : 'powerpoint');

    useEffect(() => {
        if (format === 'pdf' && !canSendPdf && canSendPpt) setFormat('powerpoint');
        if (format === 'powerpoint' && !canSendPpt && canSendPdf) setFormat('pdf');
    }, [canSendPdf, canSendPpt, format]);

    useEffect(() => {
        setReportType(isLinear ? 'Linear_report' : 'DSP_report');
    }, [isLinear]);

    const {
        start: sendPdfStart,
        requestState: sendPdfState,
        error: sendPdfError,
    } = useAPIClientRequest(ApiClient.Entities.Campaign.campaignPdfSend);
    const {
        start: sendPptStart,
        requestState: sendPptState,
        error: sendPptError,
    } = useAPIClientRequest(ApiClient.Entities.Campaign.campaignPowerpointSend);

    const sendState = format === 'pdf' ? sendPdfState : sendPptState;
    const sendError = format === 'pdf' ? sendPdfError : sendPptError;

    useEffect(() => {
        if (sendState === NetworkRequestState.Success && !sentSuccessNotified) {
            onSendSuccess?.();
            setSentSuccessNotified(true);
            setEmailsText(initiatedEmails.join('\n'));
            pcaState.setFalse();
        }
    }, [initiatedEmails, onSendSuccess, pcaState, sendState, sentSuccessNotified]);

    const isDateRangeComplete = useMemo(() => {
        const [startDate, endDate] = dateRange;
        return !!startDate && !!endDate;
    }, [dateRange]);

    const emails = useMemo(() => parseEmails(emailsText), [emailsText]);
    const disableActions = useMemo(
        () =>
            sendState === NetworkRequestState.InProgress ||
            (useDateRange === 1 && !isDateRangeComplete),
        [sendState, useDateRange, isDateRangeComplete],
    );

    const buildPayloadBase = useCallback(() => {
        const [startDate, endDate] = dateRange;
        return {
            powerpoint_template: reportType,
            skin_id: skin || 0,
            language,
            start_date:
                useDateRange === 1 && isDateRangeComplete
                    ? formatDateToDDMMYYYY(startDate)
                    : undefined,
            end_date:
                useDateRange === 1 && isDateRangeComplete
                    ? formatDateToDDMMYYYY(endDate)
                    : undefined,
            type: reportTypeToSend,
            emails,
        };
    }, [
        dateRange,
        reportType,
        skin,
        language,
        useDateRange,
        isDateRangeComplete,
        reportTypeToSend,
        emails,
    ]);

    const validate = useCallback(() => {
        if (emails.length === 0) {
            setLocalError('Please add at least one email address.');
            return false;
        }
        setLocalError(null);
        return true;
    }, [emails.length]);

    const onSend = useCallback(async () => {
        if (!validate()) return;
        setSentSuccessNotified(false);
        if (format === 'powerpoint') {
            await sendPptStart(Number(id), buildPayloadBase());
            return;
        }
        await sendPdfStart(Number(id), {
            ...buildPayloadBase(),
            third_color: thirdColor.value ? 'true' : 'false',
        });
    }, [buildPayloadBase, format, id, sendPdfStart, sendPptStart, thirdColor.value, validate]);

    const formatOptions = useMemo(
        () =>
            [
                canSendPdf && { label: 'PDF', value: 'pdf' },
                canSendPpt && { label: 'PowerPoint', value: 'powerpoint' },
            ].filter((option): option is { label: string; value: string } => Boolean(option)),
        [canSendPdf, canSendPpt],
    );

    const onSkinSelected = useCallback((val: number) => setSkin(val), []);

    const reportTypeOptions = useMemo(
        () =>
            isLinear
                ? [
                      {
                          label: t('Modules.Main.Campaigns.Overview.Pca.linearReport'),
                          value: 'Linear_report',
                      },
                  ]
                : [
                      {
                          label: t('Modules.Main.Campaigns.Overview.Pca.dspReport'),
                          value: 'DSP_report',
                      },
                  ],
        [isLinear, t],
    );

    return (
        <DialogV2 open={pcaState.value} onClose={pcaState.setFalse} title={dialogTitle}>
            <SelectField
                value={format}
                onChange={e => setFormat(e.target.value as ReportFormat)}
                options={formatOptions}
            />

            <IfHasAllPermissions permissions={[PermissionName.AdminPca_exportReport_type]}>
                <SelectField
                    options={reportTypeOptions}
                    value={reportType}
                    onChange={e =>
                        setReportType(e.target.value as 'Linear_report' | 'DSP_report' | 'custom')
                    }
                />
            </IfHasAllPermissions>

            <IfHasAllPermissions permissions={[PermissionName.Admin]}>
                <SelectField
                    value={language}
                    onChange={e => setLanguage(e.target.value as 'GB' | 'US')}
                    options={[
                        { label: 'UK', value: 'GB' },
                        { label: 'US', value: 'US' },
                    ]}
                />
            </IfHasAllPermissions>

            <SelectField
                value={useDateRange}
                onChange={e => setUseDateRange(e.target.value as number)}
                options={[
                    { label: 'Campaign Target', value: 0 },
                    { label: 'Date Range', value: 1 },
                ]}
            />

            {format === 'pdf' && (
                <IfHasAllPermissions permissions={[PermissionName.Admin]}>
                    <Checkbox
                        checked={thirdColor.value}
                        onClick={thirdColor.toggle}
                        label={t('Modules.Main.Campaigns.Overview.Pca.thirdColor')}
                    />
                </IfHasAllPermissions>
            )}

            {useDateRange === 1 && (
                <DateRangePicker
                    value={dateRange}
                    onChange={newValue => setDateRange(newValue)}
                    localeText={{ start: 'Start date', end: 'End date' }}
                    slotProps={{ textField: { fullWidth: true } }}
                />
            )}

            {adminPerms && (
                <Stack>
                    <AdminSkinSwitcher
                        disabled={disableActions}
                        onChange={onSkinSelected}
                        additionalOptions={[]}
                        selectedSkinId={skin ?? DEFAULT_SKIN_ID}
                    />
                </Stack>
            )}

            <TextField
                label="Email Addresses (one per line)"
                multiline
                minRows={4}
                value={emailsText}
                onChange={e => setEmailsText(e.target.value)}
                fullWidth
            />

            <IfHasAllPermissions permissions={format === 'pdf' ? pdfPerms : pptPerms}>
                <Button onClick={onSend} disabled={disableActions}>
                    Send
                </Button>
            </IfHasAllPermissions>

            {localError && <Alert severity="error">{localError}</Alert>}
            {sendError && <Alert severity="error">{String(sendError)}</Alert>}
            {sendState === NetworkRequestState.Success && (
                <Alert severity="success">
                    {format === 'pdf'
                        ? 'PDF sent successfully.'
                        : 'PowerPoint queued, it will be emailed shortly.'}
                </Alert>
            )}
        </DialogV2>
    );
};

export default DataStatusSendReportDialog;
